import { css, useTheme } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ChangePlanModal } from '@/components/admin/change-plan-modal';
import { DeleteUserModal } from '@/components/admin/delete-user-modal';
import { StatusActionModal } from '@/components/admin/status-action-modal';
import { UserActionsMenu } from '@/components/admin/user-actions-menu';
import {
  deleteUser,
  fetchAdminUsers,
  fetchPlans,
  updateUserPlan,
  updateUserStatus,
} from '@/lib/api-client';
import type { AdminUser, Plan } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { fontSizes, radii, spacing } from '@/theme/tokens';

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

type ModalState =
  | { type: 'none' }
  | { type: 'changePlan'; user: AdminUser }
  | { type: 'status'; user: AdminUser; action: 'activate' | 'cancel' | 'disable' }
  | { type: 'delete'; user: AdminUser };

export function AdminUsers() {
  const theme = useTheme();
  const [userList, setUserList] = useState<AdminUser[]>([]);
  const [planList, setPlanList] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState>({ type: 'none' });
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchInput]);

  const loadUsers = useCallback(
    async (cursor?: string) => {
      try {
        if (!cursor) setLoading(true);
        setError(null);
        const result = await fetchAdminUsers({
          search: debouncedSearch || undefined,
          cursor,
        });
        if (cursor) {
          setUserList((prev) => [...prev, ...result.users]);
        } else {
          setUserList(result.users);
        }
        setNextCursor(result.nextCursor);
      } catch {
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch],
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    fetchPlans()
      .then(setPlanList)
      .catch(() => {});
  }, []);

  function closeModal() {
    setModalState({ type: 'none' });
  }

  async function handleChangePlan(planId: string) {
    if (modalState.type !== 'changePlan') return;
    await updateUserPlan(modalState.user.id, planId);
    closeModal();
    loadUsers();
  }

  async function handleStatusAction() {
    if (modalState.type !== 'status') return;
    await updateUserStatus(modalState.user.id, modalState.action);
    closeModal();
    loadUsers();
  }

  async function handleSoftDelete() {
    if (modalState.type !== 'delete') return;
    await updateUserStatus(modalState.user.id, 'disable');
    closeModal();
    loadUsers();
  }

  async function handleHardDelete() {
    if (modalState.type !== 'delete') return;
    await deleteUser(modalState.user.id);
    closeModal();
    loadUsers();
  }

  return (
    <div
      css={css`
        padding: ${spacing.lg};
      `}
    >
      <h1
        css={css`
          margin: 0 0 ${spacing.lg};
          font-size: ${fontSizes.xxl};
          color: ${theme.colors.text};
        `}
      >
        Manage Users
      </h1>

      <input
        type="text"
        placeholder="Search by email..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        css={css`
          width: 100%;
          max-width: 400px;
          padding: ${spacing.sm} ${spacing.md};
          margin-bottom: ${spacing.lg};
          border: 1px solid ${theme.colors.border};
          border-radius: ${radii.sm};
          font-size: ${fontSizes.md};
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
          &:focus {
            outline: none;
            border-color: ${theme.colors.primary};
          }
        `}
      />

      {error && (
        <p
          css={css`
            color: ${theme.colors.error};
            margin-bottom: ${spacing.md};
          `}
        >
          {error}
        </p>
      )}

      {loading && <p>Loading...</p>}

      {!loading && userList.length === 0 && <p>No users found</p>}

      {!loading && userList.length > 0 && (
        <table
          css={css`
            width: 100%;
            border-collapse: collapse;

            th,
            td {
              padding: ${spacing.sm} ${spacing.md};
              text-align: left;
              border-bottom: 1px solid ${theme.colors.border};
            }

            th {
              font-weight: 600;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
              background: ${theme.colors.surface};
            }

            tbody tr:hover {
              background: ${theme.colors.surface};
            }
          `}
        >
          <thead>
            <tr>
              <th>Email</th>
              <th>Clerk</th>
              <th>Stripe</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Projects</th>
              <th>Minutes</th>
              <th>Last Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {userList.map((user) => (
              <tr
                key={user.id}
                css={
                  user.subscriptionStatus === 'disabled'
                    ? css`
                        opacity: 0.5;
                      `
                    : undefined
                }
              >
                <td>{user.email}</td>
                <td>{user.hasClerkAccount ? '\u2713' : '\u2014'}</td>
                <td>{user.hasStripeCustomer ? '\u2713' : '\u2014'}</td>
                <td>{user.planDisplayName ?? '\u2014'}</td>
                <td>
                  {user.subscriptionStatus ?? '\u2014'}
                  {user.subscriptionStatus === 'disabled' && (
                    <span
                      css={css`
                        margin-left: ${spacing.xs};
                        padding: 1px ${spacing.xs};
                        font-size: ${fontSizes.xs};
                        background: ${theme.colors.error};
                        color: white;
                        border-radius: 3px;
                      `}
                    >
                      Disabled
                    </span>
                  )}
                </td>
                <td>{user.projectCount}</td>
                <td>{Number(user.generatedMinutesThisMonth).toFixed(1)}</td>
                <td>{formatRelativeDate(user.lastProjectUpdatedAt)}</td>
                <td>
                  <UserActionsMenu
                    subscriptionStatus={user.subscriptionStatus}
                    onChangePlan={() => setModalState({ type: 'changePlan', user })}
                    onChangeStatus={(action) => setModalState({ type: 'status', user, action })}
                    onDelete={() => setModalState({ type: 'delete', user })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {nextCursor && (
        <Button
          variant="secondary"
          shape="rect"
          onClick={() => loadUsers(nextCursor)}
          css={css`
            margin-top: ${spacing.lg};
          `}
        >
          Load more
        </Button>
      )}

      {modalState.type === 'changePlan' && (
        <ChangePlanModal
          isOpen
          onClose={closeModal}
          onSave={handleChangePlan}
          plans={planList}
          currentPlanName={modalState.user.planName}
          userEmail={modalState.user.email}
        />
      )}

      {modalState.type === 'status' && (
        <StatusActionModal
          isOpen
          onClose={closeModal}
          onConfirm={handleStatusAction}
          action={modalState.action}
          userEmail={modalState.user.email}
        />
      )}

      {modalState.type === 'delete' && (
        <DeleteUserModal
          isOpen
          onClose={closeModal}
          onSoftDelete={handleSoftDelete}
          onHardDelete={handleHardDelete}
          userEmail={modalState.user.email}
        />
      )}
    </div>
  );
}
